(() => {
  'use strict';

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  };

  onReady(() => {
    root.classList.add('pbc-premium-ready');
    setupSoldOutMarkers();
    setupReactiveBackground();

    if (!reducedMotion.matches) {
      setupShootingStars();
    }

    if (!reducedMotion.matches && finePointer.matches) {
      setupStarCursor();
    }
  });

  function setupSoldOutMarkers() {
    const soldOutText = /\b(sold\s*out|out\s*of\s*stock|unavailable)\b/i;
    const sashTargets = new Set();
    const sashResizeObserver = 'ResizeObserver' in window
      ? new ResizeObserver((entries) => {
          entries.forEach((entry) => syncSashMetrics(entry.target));
        })
      : null;

    const hasSoldOutText = (element) => {
      if (!element) return false;
      return soldOutText.test((element.textContent || '').replace(/\s+/g, ' ').trim());
    };

    const syncSashMetrics = (target) => {
      if (!target) return;

      const rect = target.getBoundingClientRect();
      if (rect.width < 24 || rect.height < 24) return;

      const diagonal = Math.hypot(rect.width, rect.height);
      const angle = -Math.atan2(rect.height, rect.width) * (180 / Math.PI);
      const thickness = Math.min(Math.max(Math.min(rect.width, rect.height) * 0.145, 40), 86);

      target.style.setProperty('--pbc-sash-length', `${(diagonal * 1.04).toFixed(1)}px`);
      target.style.setProperty('--pbc-sash-angle', `${angle.toFixed(2)}deg`);
      target.style.setProperty('--pbc-sash-thickness', `${thickness.toFixed(1)}px`);
    };

    const watchSashTarget = (target) => {
      if (!target || sashTargets.has(target)) return;

      target.classList.add('pbc-sash-target');
      sashTargets.add(target);
      sashResizeObserver?.observe(target);
      syncSashMetrics(target);
      window.requestAnimationFrame(() => syncSashMetrics(target));
      window.setTimeout(() => syncSashMetrics(target), 300);
    };

    const setStrike = (owner, target, isSoldOut) => {
      if (!target) {
        owner.classList.toggle('pbc-is-sold-out', false);
        owner.querySelectorAll('.pbc-sold-out-strike').forEach((strike) => strike.remove());
        return;
      }

      let strike = Array.from(target.children).find((child) => child.classList.contains('pbc-sold-out-strike'));

      owner.classList.toggle('pbc-is-sold-out', isSoldOut);
      target.classList.toggle('pbc-sash-target', isSoldOut);

      if (isSoldOut && !strike) {
        strike = document.createElement('span');
        strike.className = 'pbc-sold-out-strike';
        strike.setAttribute('aria-hidden', 'true');
        target.appendChild(strike);
      }

      if (isSoldOut) {
        watchSashTarget(target);
      }

      if (!isSoldOut && strike) {
        strike.remove();
      }
    };

    const setCardStrike = (wrapper, isSoldOut) => {
      const target = wrapper.querySelector('.card__inner') || wrapper.querySelector('.card') || wrapper;
      setStrike(wrapper, target, isSoldOut);
    };

    const findProductSashTarget = (product) => {
      const candidates = Array.from(product.querySelectorAll('.product__media, .product-media-container, .media'))
        .filter((candidate) => {
          if (!candidate.querySelector('img')) return false;
          if (candidate.closest('.thumbnail-list, .thumbnail-slider, product-recommendations')) return false;

          const rect = candidate.getBoundingClientRect();
          return rect.width > 80 && rect.height > 80;
        })
        .map((candidate) => {
          const rect = candidate.getBoundingClientRect();
          return {
            candidate,
            area: rect.width * rect.height,
          };
        })
        .sort((a, b) => b.area - a.area);

      return candidates[0]?.candidate || product.querySelector('.product__media-wrapper');
    };

    const refresh = () => {
      document.querySelectorAll('.card-wrapper, .product-card-wrapper').forEach((wrapper) => {
        const badges = Array.from(wrapper.querySelectorAll('.badge, .card__badge, [class*="badge"]'));
        const buttons = Array.from(wrapper.querySelectorAll('button, .button, [aria-disabled="true"]'));
        const badgeSaysSoldOut = badges.some((badge) => hasSoldOutText(badge));
        const disabledSoldOutButton = buttons.some((button) => {
          const disabled = button.disabled || button.getAttribute('aria-disabled') === 'true';
          return disabled && hasSoldOutText(button);
        });

        setCardStrike(wrapper, badgeSaysSoldOut || disabledSoldOutButton);
      });

      document.querySelectorAll('.product').forEach((product) => {
        const addButton = product.querySelector('.product-form__submit, button[name="add"]');
        const isSoldOut = Boolean(
          addButton &&
          (addButton.disabled || addButton.getAttribute('aria-disabled') === 'true') &&
          hasSoldOutText(addButton)
        );

        product.classList.toggle('pbc-is-sold-out', isSoldOut);
        const productTarget = findProductSashTarget(product);

        product.querySelectorAll('.pbc-sold-out-strike').forEach((strike) => {
          if (!isSoldOut || strike.parentElement !== productTarget) {
            strike.remove();
          }
        });

        setStrike(product, productTarget, isSoldOut);
      });
    };

    let scheduled = false;
    const scheduleRefresh = () => {
      if (scheduled) return;
      scheduled = true;

      window.requestAnimationFrame(() => {
        scheduled = false;
        refresh();
      });
    };

    refresh();

    const observer = new MutationObserver(scheduleRefresh);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled', 'data-available', 'data-product-available'],
    });

    document.addEventListener('shopify:section:load', scheduleRefresh);
    document.addEventListener('variant:change', scheduleRefresh);
    document.addEventListener('change', scheduleRefresh, true);
    window.addEventListener('resize', scheduleRefresh, { passive: true });
  }

  function setupReactiveBackground() {
    let pointerX = 0;
    let pointerY = 0;
    let scrollY = 0;
    let scheduled = false;

    const commit = () => {
      scheduled = false;
      root.style.setProperty('--pbc-bg-x', `${pointerX.toFixed(1)}px`);
      root.style.setProperty('--pbc-bg-y', `${(pointerY + scrollY).toFixed(1)}px`);
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(commit);
    };

    if (finePointer.matches) {
      window.addEventListener('pointermove', (event) => {
        pointerX = ((event.clientX / window.innerWidth) - 0.5) * 34;
        pointerY = ((event.clientY / window.innerHeight) - 0.5) * 24;
        schedule();
      }, { passive: true });
    }

    window.addEventListener('scroll', () => {
      scrollY = Math.min(18, window.scrollY * 0.012);
      schedule();
    }, { passive: true });
  }

  function setupStarCursor() {
    const cursor = document.createElement('div');
    cursor.className = 'pbc-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.innerHTML = '<span class="pbc-cursor__star"></span>';
    document.body.appendChild(cursor);

    root.classList.add('pbc-custom-cursor-active');

    const interactiveSelector = [
      'a',
      'button',
      'input',
      'textarea',
      'select',
      'summary',
      'label',
      '[role="button"]',
      '[tabindex]:not([tabindex="-1"])',
      '.shopify-payment-button__button',
    ].join(',');

    let x = -100;
    let y = -100;
    let scheduled = false;

    const move = () => {
      scheduled = false;
      cursor.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
    };

    const scheduleMove = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(move);
    };

    document.addEventListener('pointermove', (event) => {
      x = event.clientX;
      y = event.clientY;
      cursor.classList.add('is-visible');
      cursor.classList.toggle('is-hovering', Boolean(event.target.closest(interactiveSelector)));
      scheduleMove();
    }, { passive: true });

    document.addEventListener('pointerleave', () => {
      cursor.classList.remove('is-visible');
    });

    document.addEventListener('pointerdown', (event) => {
      cursor.classList.add('is-clicking');
      createClickBurst(event.clientX, event.clientY);
    }, { passive: true });

    document.addEventListener('pointerup', () => {
      window.setTimeout(() => cursor.classList.remove('is-clicking'), 90);
    }, { passive: true });
  }

  function createClickBurst(x, y) {
    const count = 9;

    for (let index = 0; index < count; index += 1) {
      const star = document.createElement('i');
      const angle = ((Math.PI * 2) / count) * index + Math.random() * 0.45;
      const distance = 30 + Math.random() * 46;

      star.className = 'pbc-click-star';
      star.setAttribute('aria-hidden', 'true');
      star.style.setProperty('--pbc-click-x', `${x}px`);
      star.style.setProperty('--pbc-click-y', `${y}px`);
      star.style.setProperty('--pbc-click-dx', `${Math.cos(angle) * distance}px`);
      star.style.setProperty('--pbc-click-dy', `${Math.sin(angle) * distance}px`);

      document.body.appendChild(star);
      window.setTimeout(() => star.remove(), 680);
    }
  }

  function setupShootingStars() {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { alpha: true });

    if (!context) return;

    canvas.className = 'pbc-starfield';
    canvas.setAttribute('aria-hidden', 'true');
    Object.assign(canvas.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '-1',
      width: '100vw',
      height: '100vh',
      display: 'block',
      pointerEvents: 'none',
      opacity: '0.96',
      mixBlendMode: 'screen',
    });

    document.documentElement.style.background = '#020307';
    document.body.style.backgroundColor = 'transparent';
    document.body.style.overflowX = 'hidden';
    document.body.style.isolation = 'isolate';

    document.body.prepend(canvas);

    let width = 0;
    let height = 0;
    let ratio = 1;
    let lastFrame = performance.now();
    let nextShot = 8;
    let sparkles = [];
    let shots = [];
    let pointer = {
      x: 0.5,
      y: 0.5,
      active: false,
    };

    const resize = () => {
      ratio = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;

      canvas.width = Math.ceil(width * ratio);
      canvas.height = Math.ceil(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);

      const count = Math.round(Math.min(210, Math.max(92, (width * height) / 12500)));
      sparkles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: 0.32 + Math.random() * 1.42,
        alpha: 0.28 + Math.random() * 0.62,
        phase: Math.random() * Math.PI * 2,
        speed: 0.24 + Math.random() * 0.95,
      }));
    };

    const spawnShot = (originX, originY, compact = false) => {
      const speed = compact ? 4.4 + Math.random() * 2.8 : 5.8 + Math.random() * 4.1;
      const angle = -0.42 - Math.random() * 0.28;

      shots.push({
        x: originX ?? (-120 + Math.random() * width * 0.58),
        y: originY ?? (height * (0.34 + Math.random() * 0.58)),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0,
        maxLife: compact ? 40 + Math.random() * 24 : 68 + Math.random() * 44,
        length: compact ? 54 + Math.random() * 46 : 112 + Math.random() * 94,
        width: compact ? 1.2 + Math.random() * 1.1 : 1.6 + Math.random() * 2.1,
        alpha: compact ? 0.62 : 0.88 + Math.random() * 0.3,
      });
    };

    let lastPointerShot = 0;

    if (finePointer.matches) {
      window.addEventListener('pointermove', (event) => {
        pointer = {
          x: event.clientX / Math.max(width, 1),
          y: event.clientY / Math.max(height, 1),
          active: true,
        };

        const now = performance.now();
        if (now - lastPointerShot > 260 && Math.random() > 0.24) {
          lastPointerShot = now;
          spawnShot(event.clientX - 38 + Math.random() * 76, event.clientY + 14 + Math.random() * 40, true);
        }
      }, { passive: true });

      window.addEventListener('pointerleave', () => {
        pointer.active = false;
      }, { passive: true });
    }

    const drawSparkles = (now) => {
      const parallaxX = (pointer.x - 0.5) * 18;
      const parallaxY = (pointer.y - 0.5) * 12;

      sparkles.forEach((star) => {
        const twinkle = 0.55 + Math.sin(now * 0.001 * star.speed + star.phase) * 0.45;
        const alpha = Math.max(0, star.alpha * twinkle);
        const x = star.x + parallaxX * (star.radius / 1.4);
        const y = star.y + parallaxY * (star.radius / 1.4);

        context.beginPath();
        context.fillStyle = `rgba(255, 157, 225, ${alpha})`;
        context.arc(x, y, star.radius, 0, Math.PI * 2);
        context.fill();
      });
    };

    const drawPointerGlow = () => {
      if (!pointer.active) return;

      const glowX = pointer.x * width;
      const glowY = pointer.y * height;
      const gradient = context.createRadialGradient(glowX, glowY, 0, glowX, glowY, 120);
      gradient.addColorStop(0, 'rgba(255, 157, 225, 0.16)');
      gradient.addColorStop(0.48, 'rgba(245, 72, 183, 0.06)');
      gradient.addColorStop(1, 'rgba(245, 72, 183, 0)');

      context.fillStyle = gradient;
      context.fillRect(glowX - 125, glowY - 125, 250, 250);
    };

    const drawShots = (delta) => {
      shots = shots.filter((shot) => {
        shot.x += shot.vx * delta;
        shot.y += shot.vy * delta;
        shot.life += delta;

        const magnitude = Math.hypot(shot.vx, shot.vy) || 1;
        const tailX = shot.x - (shot.vx / magnitude) * shot.length;
        const tailY = shot.y - (shot.vy / magnitude) * shot.length;
        const fade = Math.max(0, 1 - shot.life / shot.maxLife);
        const gradient = context.createLinearGradient(tailX, tailY, shot.x, shot.y);

        gradient.addColorStop(0, 'rgba(255, 77, 189, 0)');
        gradient.addColorStop(0.48, `rgba(255, 77, 189, ${0.36 * shot.alpha * fade})`);
        gradient.addColorStop(0.82, `rgba(255, 213, 242, ${0.88 * shot.alpha * fade})`);
        gradient.addColorStop(1, `rgba(255, 255, 255, ${0.92 * shot.alpha * fade})`);

        context.beginPath();
        context.strokeStyle = gradient;
        context.lineWidth = shot.width;
        context.lineCap = 'round';
        context.moveTo(tailX, tailY);
        context.lineTo(shot.x, shot.y);
        context.stroke();

        context.beginPath();
        context.fillStyle = `rgba(255, 213, 242, ${0.8 * fade})`;
        context.arc(shot.x, shot.y, shot.width * 1.4, 0, Math.PI * 2);
        context.fill();

        return shot.life < shot.maxLife && shot.x < width + 180 && shot.y > -180;
      });
    };

    const frame = (now) => {
      const delta = Math.min(2.2, (now - lastFrame) / 16.67);
      lastFrame = now;

      context.clearRect(0, 0, width, height);
      drawSparkles(now);
      drawPointerGlow();

      nextShot -= delta;
      if (nextShot <= 0) {
        spawnShot();
        if (Math.random() > 0.72) {
          spawnShot(-140 + Math.random() * width * 0.7, height * (0.22 + Math.random() * 0.64), Math.random() > 0.45);
        }
        if (Math.random() > 0.9) {
          spawnShot(-120 + Math.random() * width * 0.64, height * (0.24 + Math.random() * 0.58), true);
        }
        nextShot = 18 + Math.random() * 52;
      }

      drawShots(delta);
      window.requestAnimationFrame(frame);
    };

    resize();
    window.addEventListener('resize', resize, { passive: true });
    window.requestAnimationFrame(frame);
  }
})();
